import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { adminService } from '../services/admin.service';
import StatsCard from '../components/dashboard/StatsCard';
import ServiceStatusChart from '../components/dashboard/ServiceStatusChart';
import ServiceTrendsChart from '../components/dashboard/ServiceTrendsChart';
import RecentAlerts from '../components/dashboard/RecentAlerts';
import SlaWatchlist from '../components/dashboard/SlaWatchlist';
import LiveJobsSection from '../components/dashboard/LiveJobsSection';
import LoadingSpinner from '../components/common/LoadingSpinner';

const formatMinutes = (minutes) => {
    if (minutes == null || Number.isNaN(Number(minutes))) return '—';
    const m = Number(minutes);
    if (m < 60) return `${m.toFixed(1)} min`;
    const hours = m / 60;
    return `${hours.toFixed(1)} hr`;
};

const Dashboard = () => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const data = await adminService.getDashboardAnalytics();
            setAnalytics(data);
        } catch (error) {
            console.error('Failed to fetch analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (!analytics) {
        return <div className="text-center py-8 text-gray-500">Failed to load analytics</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center title-bgWrap mrgall padlr15 border bxshadowhd">
                <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
                <button
                    onClick={fetchAnalytics}
                    className="flex items-center gap-2 px-4 py-2 bg-scarlet-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
                >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            {/* Lifetime / live overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mrgall">
                <StatsCard
                    title="Total Users"
                    value={analytics.total_users}
                    icon="users"
                    trend={analytics.users_growth_percent}
                    trendLabel="vs prior 30 days"
                    color="blue"
                />
                <StatsCard
                    title="Active Vendors"
                    value={analytics.total_vendors}
                    icon="truck"
                    subtitle={`${analytics.total_drivers} drivers`}
                    color="green"
                />
                <StatsCard
                    title="Active Services"
                    value={analytics.active_services}
                    icon="clock"
                    subtitle={`${analytics.pending_assignments} pending`}
                    color="yellow"
                />
                <StatsCard
                    title="Completed (All Time)"
                    value={analytics.completed_services}
                    icon="check-circle"
                    percentage={
                        analytics.total_services > 0
                            ? Math.round((analytics.completed_services / analytics.total_services) * 100)
                            : 0
                    }
                    color="green"
                />
            </div>

            {/* Platform health — month to date */}
            <div className="mrgall">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 px-1">Platform Health (This Month)</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-6">
                    <StatsCard
                        title="Completed This Month"
                        value={analytics.completed_services_mtd ?? 0}
                        icon="check-circle"
                        trend={analytics.completed_mtd_change_percent}
                        color="green"
                    />
                    <StatsCard
                        title="Created This Month"
                        value={analytics.created_services_mtd ?? 0}
                        icon="file-plus"
                        subtitle={`${analytics.cancelled_services_mtd ?? 0} cancelled`}
                        color="blue"
                    />
                    <StatsCard
                        title="Active + Pending"
                        value={analytics.active_services}
                        icon="clock"
                        subtitle={`${analytics.pending_assignments} pending`}
                        color="yellow"
                    />
                    <StatsCard
                        title="Completion Rate (MTD)"
                        value={analytics.completion_rate_mtd ?? 0}
                        icon="percent"
                        valueSuffix="%"
                        subtitle={`${analytics.completed_services_mtd ?? 0} done / ${(analytics.completed_services_mtd ?? 0) + (analytics.cancelled_services_mtd ?? 0)} closed`}
                        color="cyan"
                    />
                    <StatsCard
                        title="Avg Response Time"
                        value={formatMinutes(analytics.avg_response_time_minutes_mtd)}
                        icon="timer"
                        subtitle="Create → assign / alert"
                        color="indigo"
                    />
                    <StatsCard
                        title="Avg Completion Time"
                        value={formatMinutes(analytics.avg_completion_time_minutes_mtd)}
                        icon="timer"
                        subtitle="Create → complete"
                        color="amber"
                    />
                    <StatsCard
                        title="Avg CSAT (MTD)"
                        value={analytics.avg_csat_mtd ? Number(analytics.avg_csat_mtd).toFixed(1) : '—'}
                        icon="star"
                        valueSuffix={analytics.avg_csat_mtd ? '/ 5' : ''}
                        subtitle="Customer feedback"
                        color="green"
                    />
                </div>
            </div>

            <LiveJobsSection />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mrgall">
                <ServiceStatusChart data={analytics} />
                <ServiceTrendsChart trends={analytics.service_trends || []} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mrgall">
                <RecentAlerts alerts={analytics.recent_alerts} />
                <SlaWatchlist />
            </div>
        </div>
    );
};

export default Dashboard;
